import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Brain, Home, Package, BarChart3, Users, Settings, DollarSign, LineChart as LineChartIcon } from 'lucide-react';
import { collection, getDocs, doc, getDoc, query, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import AISystemAdmin from '../admin/AISystemAdmin';
import CoursedeeOrdersAdmin from '../../components/CoursedeeOrdersAdmin';
import CoursedeeAnalytics from '../../components/CoursedeeAnalytics';
import CoursedeeUsersAdmin from '../../components/CoursedeeUsersAdmin';

type AdminSection = 'packages' | 'orders' | 'analytics' | 'users' | 'cost' | 'usercost' | 'settings';

const baht = (n: number) => `฿${(n || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 })}`;
const num = (n: number) => (n || 0).toLocaleString('th-TH');

// หน้าแสดงต้นทุน AI + กราฟรายวัน
function AICostView() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<any>(null);
  const [month, setMonth] = useState<any>(null);
  const [daily, setDaily] = useState<any[]>([]);
  const monthKey = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    // real-time listeners — อัปเดตทันทีไม่ต้องรีเฟรช
    const unsubSummary = onSnapshot(doc(db, 'coursedee_ai_usage', 'summary'), (s) => {
      setSummary(s.exists() ? s.data() : {});
      setLoading(false);
    }, () => setLoading(false));
    const unsubMonth = onSnapshot(doc(db, 'coursedee_ai_usage', monthKey), (m) => setMonth(m.exists() ? m.data() : {}));
    const unsubDaily = onSnapshot(query(collection(db, 'coursedee_ai_usage_daily'), orderBy('date')), (d) => {
      setDaily(
        d.docs.map((doc) => {
          const x = doc.data();
          return { date: (x.date || doc.id).slice(5), cost: Number((x.totalCostTHB || 0).toFixed(2)), calls: x.totalCalls || 0 };
        })
      );
    });
    return () => { unsubSummary(); unsubMonth(); unsubDaily(); };
  }, [monthKey]);

  if (loading) return <div className="bg-white rounded-2xl p-8 text-center text-gray-500">กำลังโหลด...</div>;

  const Card = ({ label, value, sub, color }: any) => (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className={`text-2xl md:text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card label="ต้นทุนรวมทั้งหมด" value={baht(summary?.totalCostTHB)} sub={`${num(summary?.totalCalls)} ครั้ง`} color="text-red-600" />
        <Card label={`ต้นทุนเดือนนี้ (${monthKey})`} value={baht(month?.totalCostTHB)} sub={`${num(month?.totalCalls)} ครั้ง`} color="text-orange-600" />
        <Card label="Input tokens (รวม)" value={num(summary?.totalInputTokens)} color="text-indigo-600" />
        <Card label="Output tokens (รวม)" value={num(summary?.totalOutputTokens)} color="text-purple-600" />
      </div>

      {/* กราฟต้นทุนรายวัน */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-bold text-gray-900 mb-4">📈 ต้นทุนรายวัน (บาท)</h3>
        {daily.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">ยังไม่มีข้อมูล — เริ่มเจนคอนเทนต์แล้วกราฟจะขึ้น</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={daily}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: any) => [`฿${v}`, 'ต้นทุน']} labelFormatter={(l) => `วันที่ ${l}`} />
              <Bar dataKey="cost" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h3 className="font-bold text-gray-900 mb-3">รายละเอียด</h3>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex justify-between"><span>จำนวนการเจนทั้งหมด</span><span className="font-semibold text-gray-900">{num(summary?.totalCalls)} ครั้ง</span></div>
          <div className="flex justify-between"><span>ต้นทุนเฉลี่ยต่อครั้ง</span><span className="font-semibold text-gray-900">{summary?.totalCalls ? baht((summary.totalCostTHB || 0) / summary.totalCalls) : '฿0'}</span></div>
          <div className="flex justify-between"><span>อัปเดตล่าสุด</span><span className="font-semibold text-gray-900">{summary?.updatedAt ? new Date(summary.updatedAt).toLocaleString('th-TH') : '-'}</span></div>
        </div>
        <p className="text-xs text-gray-400 mt-4">
          * คำนวณจาก token จริง (gemini-2.5-flash: input $0.30 / output $2.50 ต่อ 1M tokens · เรท ฿35/$) — เป็นค่าประมาณ ยอดจริงดูที่ Google Cloud Billing
        </p>
      </div>
    </div>
  );
}

// หน้าต้นทุนรายคน — ใครเจนไปกี่ครั้ง ต้นทุนเท่าไหร่
function AIUserCostView() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    // real-time — อัปเดตทันทีไม่ต้องรีเฟรช
    const unsub = onSnapshot(
      collection(db, 'coursedee_ai_usage_users'),
      (snap) => {
        const data = snap.docs
          .map((d) => d.data())
          .sort((a: any, b: any) => (b.totalCostTHB || 0) - (a.totalCostTHB || 0));
        setRows(data);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  if (loading) return <div className="bg-white rounded-2xl p-8 text-center text-gray-500">กำลังโหลด...</div>;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <h3 className="font-bold text-gray-900 mb-1">ต้นทุนรายผู้ใช้ (AIForYou V1)</h3>
      <p className="text-sm text-gray-500 mb-4">เรียงจากต้นทุนสูงสุด · {rows.length} คน</p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-8">ยังไม่มีข้อมูล</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4">#</th>
                <th className="py-2 pr-4">ผู้ใช้</th>
                <th className="py-2 pr-4 text-right">จำนวนเจน</th>
                <th className="py-2 pr-4 text-right">Tokens (in/out)</th>
                <th className="py-2 text-right">ต้นทุน</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.username || i} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="py-2 pr-4 text-gray-400">{i + 1}</td>
                  <td className="py-2 pr-4 font-medium text-gray-900">{r.username || '-'}</td>
                  <td className="py-2 pr-4 text-right">{num(r.totalCalls)}</td>
                  <td className="py-2 pr-4 text-right text-gray-500">{num(r.totalInputTokens)} / {num(r.totalOutputTokens)}</td>
                  <td className="py-2 text-right font-semibold text-red-600">{baht(r.totalCostTHB)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function CoursedeeAdmin() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<AdminSection>('packages');
  const [stats, setStats] = useState({
    totalPackages: 0,
    activePackages: 0,
    totalUsers: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Load AI packages
      const packagesSnap = await getDocs(collection(db, 'aiPackages'));
      const totalPackages = packagesSnap.size;
      const activePackages = packagesSnap.docs.filter(doc => doc.data().active).length;

      // Load Coursedee users (V1 + V2)
      const v1UsersSnap = await getDocs(collection(db, 'coursedee_v1_users'));
      const v2UsersSnap = await getDocs(collection(db, 'coursedee_v2_users'));
      const totalUsers = v1UsersSnap.size + v2UsersSnap.size;

      setStats({
        totalPackages,
        activePackages,
        totalUsers
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const menuItems = [
    { id: 'packages' as AdminSection, label: 'จัดการแพ็คเก็จ AI', icon: Package },
    { id: 'orders' as AdminSection, label: 'คำสั่งซื้อ', icon: BarChart3 },
    { id: 'analytics' as AdminSection, label: 'วิเคราะห์ข้อมูล', icon: BarChart3 },
    { id: 'users' as AdminSection, label: 'ผู้ใช้งาน', icon: Users },
    { id: 'cost' as AdminSection, label: 'ต้นทุน AI + กราฟ', icon: LineChartIcon },
    { id: 'usercost' as AdminSection, label: 'ต้นทุนรายคน', icon: DollarSign },
    { id: 'settings' as AdminSection, label: 'ตั้งค่า', icon: Settings },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'packages':
        return <AISystemAdmin />;
      case 'orders':
        return <CoursedeeOrdersAdmin />;
      case 'analytics':
        return <CoursedeeAnalytics />;
      case 'users':
        return <CoursedeeUsersAdmin />;
      case 'cost':
        return <AICostView />;
      case 'usercost':
        return <AIUserCostView />;
      case 'settings':
        return <div className="bg-white rounded-2xl p-8 text-center">
          <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">ตั้งค่า</h3>
          <p className="text-gray-600">กำลังพัฒนา...</p>
        </div>;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - AIForYou</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <Brain className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Admin AIForYou</h1>
                  <p className="text-sm text-gray-600">ระบบจัดการ AIForYou</p>
                </div>
              </div>

              <button
                onClick={() => navigate('/admin/')}
                className="flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl font-medium text-gray-700 shadow-sm transition-all"
              >
                <Home className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <aside className="lg:w-64 shrink-0">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sticky top-24">
                <nav className="space-y-1">
                  {menuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    
                    return (
                      <button
                        key={item.id}
                        onClick={() => setActiveSection(item.id)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                          isActive
                            ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-500/30'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </nav>

                {/* Quick Stats */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    สถิติด่วน
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">แพ็คเก็จทั้งหมด</span>
                      <span className="font-bold text-purple-600">{stats.totalPackages}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">แพ็คเก็จเปิดใช้งาน</span>
                      <span className="font-bold text-green-600">{stats.activePackages}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">ผู้ใช้งาน</span>
                      <span className="font-bold text-indigo-600">{stats.totalUsers}</span>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            {/* Content Area */}
            <main className="flex-1 min-w-0">
              {renderContent()}
            </main>
          </div>
        </div>
      </div>
    </>
  );
}
